<div class="">
  <div class="max-content-width spaced-sections">
    @if (!project) {
      <div class="flex items-center justify-center h-64">
        <div class="text-gray-600">Loading project details...</div>
      </div>
    } @else {
      <!-- Researchers Section -->
      <section class="card pt-4 px-4 pb-2">
        <div class="flex justify-between items-center mb-3 relative">
          <div class="flex items-start gap-2">
            <button
              type="button"
              (click)="toggleSection('researchers')"
              class="rounded-lg p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary/40 transition-colors mt-0.5 flex-shrink-0"
              [attr.aria-expanded]="!isCollapsed('researchers')"
              [attr.aria-label]="
                isCollapsed('researchers')
                  ? 'Expand Project Researchers'
                  : 'Collapse Project Researchers'
              "
            >
              <svg
                class="w-5 h-5 transition-transform duration-200"
                [class.rotate-180]="!isCollapsed('researchers')"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <div>
              <h2 class="section-title">
                Project Researchers
                @if (projectResearchersTotalItems() > 0) {
                  <span class="text-base font-normal text-gray-500">
                    ({{ projectResearchersTotalItems() }})
                  </span>
                }
              </h2>
              <p class="text-sm text-gray-600 mt-1">
                View and manage the researchers of the group who are working on this project.
              </p>
            </div>
          </div>
          @if (canManageProject() && !isCollapsed('researchers')) {
            <button
              type="button"
              (click)="openAddResearcherModal()"
              class="btn-primary absolute right-0 top-0"
            >
              Add Researcher
            </button>
          }
        </div>

        @if (!isCollapsed('researchers')) {
          <div class="mb-2">
            <input
              type="search"
              class="input-field w-full"
              placeholder="Search researchers by name or email…"
              [value]="researcherSearchQuery()"
              (input)="onResearcherSearchChange($any($event.target).value)"
            />
          </div>

          @if (projectResearchers().length === 0) {
            <div class="text-center py-8">
              <p class="text-gray-600 mb-4">
                @if (researcherSearchQuery().length > 0) {
                  No researchers match your search.
                } @else if (canManageProject()) {
                  No researchers added yet. Add your first researcher to get started.
                } @else {
                  No researchers have been added to this project yet.
                }
              </p>
            </div>
          } @else {
            <div class="divide-y divide-slate-200">
              @for (researcher of projectResearchers(); track researcher.id) {
                <div
                  class="flex items-center justify-between gap-2 px-2 py-2 hover:bg-slate-50 transition-colors"
                >
                  <div class="flex items-center gap-2 min-w-0 flex-1">
                    <span class="font-medium text-gray-900 whitespace-nowrap">
                      {{ researcher.firstName }} {{ researcher.lastName || researcher.fullName }}
                    </span>
                    @if (isPiUser(researcher)) {
                      <span
                        class="inline-flex items-center gap-0.5 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-bold text-yellow-800 border border-yellow-300 whitespace-nowrap"
                      >
                        ⭐ PI
                      </span>
                    }
                    <span class="text-sm text-gray-600 truncate min-w-0">
                      {{ researcher.email || researcher.emailAddress }}
                    </span>
                  </div>
                  @if (canManageProject() && researcher.id !== userInfo?.id) {
                    <button
                      type="button"
                      (click)="openRemoveConfirm(researcher)"
                      class="btn-small-danger flex-shrink-0"
                    >
                      Remove
                    </button>
                  }
                </div>
              }
            </div>

            @if (projectResearchersTotalPages() > 1) {
              <div class="flex items-center justify-between px-2 py-3 text-sm text-gray-600">
                <button
                  type="button"
                  (click)="goToResearchersPage(projectResearchersCurrentPage() - 1)"
                  [disabled]="projectResearchersCurrentPage() === 0"
                  class="btn-small-secondary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  « Prev
                </button>
                <span
                  >Page {{ projectResearchersCurrentPage() + 1 }} of
                  {{ projectResearchersTotalPages() }}</span
                >
                <button
                  type="button"
                  (click)="goToResearchersPage(projectResearchersCurrentPage() + 1)"
                  [disabled]="projectResearchersCurrentPage() + 1 >= projectResearchersTotalPages()"
                  class="btn-small-secondary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next »
                </button>
              </div>
            }
          }
        }
      </section>

      <!-- Prerequisites Overview Cards -->
      <section class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <!-- Exclusion Reasons Card -->
        <div
          class="rounded-xl border-2 p-4 transition-colors"
          [class.border-amber-300.bg-amber-50]="exclusionReasons.length === 0 && isProjectPI()"
          [class.border-emerald-300.bg-emerald-50]="exclusionReasons.length > 0"
          [class.border-slate-200]="exclusionReasons.length === 0 && !isProjectPI()"
        >
          <div class="flex items-start gap-3">
            <div
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg font-bold"
              [class.bg-amber-200.text-amber-800]="exclusionReasons.length === 0 && isProjectPI()"
              [class.bg-emerald-200.text-emerald-800]="exclusionReasons.length > 0"
              [class.bg-slate-100.text-slate-500]="exclusionReasons.length === 0 && !isProjectPI()"
            >
              {{ exclusionReasons.length > 0 ? '&#10003;' : '!' }}
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-bold text-gray-900">Exclusion Reasons</p>
              <p
                class="mt-1 text-sm"
                [class.text-amber-800]="exclusionReasons.length === 0 && isProjectPI()"
                [class.text-emerald-800]="exclusionReasons.length > 0"
                [class.text-slate-500]="exclusionReasons.length === 0 && !isProjectPI()"
              >
                @if (exclusionReasons.length > 0) {
                  {{ exclusionReasons.length }} reason{{ exclusionReasons.length === 1 ? '' : 's' }}
                  defined for full-text screening.
                } @else if (isProjectPI()) {
                  No exclusion reasons defined yet. Define reasons before advancing to full-text
                  screening.
                } @else {
                  No exclusion reasons defined yet.
                }
              </p>
              @if (exclusionReasons.length === 0 && isProjectPI()) {
                <button
                  type="button"
                  (click)="defineExclusionReasons()"
                  class="mt-3 inline-flex items-center gap-1 rounded-lg bg-amber-200 px-3 py-1.5 text-xs font-bold text-amber-900 hover:bg-amber-300 transition-colors"
                >
                  + Define Exclusion Reasons
                </button>
              } @else if (isProjectPI()) {
                <button
                  type="button"
                  (click)="startEditingReasons()"
                  class="mt-3 inline-flex items-center gap-1 rounded-lg bg-emerald-200 px-3 py-1.5 text-xs font-bold text-emerald-900 hover:bg-emerald-300 transition-colors"
                >
                  View / Edit Reasons
                </button>
              }
            </div>
          </div>
        </div>
        <!-- Extraction Questions Card -->
        <div
          class="rounded-xl border-2 p-4 transition-colors"
          [class.border-amber-300.bg-amber-50]="extractionQuestionCount === 0 && isProjectPI()"
          [class.border-emerald-300.bg-emerald-50]="extractionQuestionCount > 0"
          [class.border-slate-200]="extractionQuestionCount === 0 && !isProjectPI()"
        >
          <div class="flex items-start gap-3">
            <div
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg font-bold"
              [class.bg-amber-200.text-amber-800]="extractionQuestionCount === 0 && isProjectPI()"
              [class.bg-emerald-200.text-emerald-800]="extractionQuestionCount > 0"
              [class.bg-slate-100.text-slate-500]="extractionQuestionCount === 0 && !isProjectPI()"
            >
              {{ extractionQuestionCount > 0 ? '&#10003;' : '!' }}
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-bold text-gray-900">Extraction Questions</p>
              <p
                class="mt-1 text-sm"
                [class.text-amber-800]="extractionQuestionCount === 0 && isProjectPI()"
                [class.text-emerald-800]="extractionQuestionCount > 0"
                [class.text-slate-500]="extractionQuestionCount === 0 && !isProjectPI()"
              >
                @if (extractionQuestionCount > 0) {
                  {{ extractionQuestionCount }} question{{
                    extractionQuestionCount === 1 ? '' : 's'
                  }}
                  configured for data extraction.
                } @else if (isProjectPI()) {
                  No extraction questions configured. Set up a questionnaire before advancing to
                  data extraction.
                } @else {
                  No extraction questions configured yet.
                }
              </p>
              @if (extractionQuestionCount === 0 && isProjectPI()) {
                <button
                  type="button"
                  (click)="openExtractionQuestions()"
                  class="mt-3 inline-flex items-center gap-1 rounded-lg bg-amber-200 px-3 py-1.5 text-xs font-bold text-amber-900 hover:bg-amber-300 transition-colors cursor-pointer"
                >
                  + Set Up Questionnaire
                </button>
              } @else if (isProjectPI()) {
                <button
                  type="button"
                  (click)="openExtractionQuestions()"
                  class="mt-3 inline-flex items-center gap-1 rounded-lg bg-emerald-200 px-3 py-1.5 text-xs font-bold text-emerald-900 hover:bg-emerald-300 transition-colors cursor-pointer"
                >
                  View / Edit Questions
                </button>
              }
            </div>
          </div>
        </div>
        <!-- Agreement Threshold Card -->
        <div
          class="rounded-xl border-2 p-4 transition-colors"
          [class.border-sky-300.bg-sky-50]="
            project.cohenKappaThreshold !== null && project.cohenKappaThreshold !== undefined
          "
          [class.border-slate-200]="
            project.cohenKappaThreshold === null || project.cohenKappaThreshold === undefined
          "
        >
          <div class="flex items-start gap-3">
            <div
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg font-bold"
              [class.bg-sky-200.text-sky-800]="
                project.cohenKappaThreshold !== null && project.cohenKappaThreshold !== undefined
              "
              [class.bg-slate-100.text-slate-500]="
                project.cohenKappaThreshold === null || project.cohenKappaThreshold === undefined
              "
            >
              k
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-bold text-gray-900">Agreement Threshold</p>
              <p
                class="mt-1 text-sm"
                [class.text-sky-800]="
                  project.cohenKappaThreshold !== null && project.cohenKappaThreshold !== undefined
                "
                [class.text-slate-500]="
                  project.cohenKappaThreshold === null || project.cohenKappaThreshold === undefined
                "
              >
                @if (
                  project.cohenKappaThreshold !== null && project.cohenKappaThreshold !== undefined
                ) {
                  Cohen's kappa threshold:
                  {{ project.cohenKappaThreshold | number: '1.2-2' }}.
                } @else if (canConfigureAgreementThreshold()) {
                  No agreement threshold configured. You can optionally define one below until
                  Title/Abstract Screening is completed.
                } @else {
                  No agreement threshold configured.
                }
              </p>
            </div>
          </div>
        </div>
      </section>

      <app-protocol-card
        [protocol]="protocol"
        [isEditable]="isProjectPI() && !isProtocolLocked()"
        [isLocked]="isProtocolLocked()"
        [saving]="savingProtocol"
        [collapsed]="isCollapsed('protocol')"
        (save)="onProtocolSave($event)"
        (collapsedChange)="toggleSection('protocol')"
      ></app-protocol-card>

      <!-- Screening Criteria Section -->
      <section class="card card-padding">
        <div class="flex justify-between items-start mb-4">
          <div class="flex items-start gap-2">
            <button
              type="button"
              (click)="toggleSection('criteria')"
              class="rounded-lg p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary/40 transition-colors mt-0.5 flex-shrink-0"
              [attr.aria-expanded]="!isCollapsed('criteria')"
              [attr.aria-label]="
                isCollapsed('criteria')
                  ? 'Expand Screening Criteria'
                  : 'Collapse Screening Criteria'
              "
            >
              <svg
                class="w-5 h-5 transition-transform duration-200"
                [class.rotate-180]="!isCollapsed('criteria')"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <div>
              <div class="flex items-center gap-1.5">
                <h2 class="section-title">Screening Criteria</h2>
                <app-info-icon
                  heading="Screening Criteria"
                  content="Define which papers are eligible for your review. Each criterion should be a single, unambiguous rule screeners can apply consistently."
                  guideAnchor="screening-criteria"
                ></app-info-icon>
              </div>
              <p class="text-sm text-gray-600 mt-1">
                Define the rules that determine whether a paper qualifies for your review. Inclusion
                criteria are conditions a paper must meet; exclusion criteria are conditions that
                disqualify it.
              </p>
            </div>
          </div>
          @if (!isEditingCriteria && canEditCriteria() && !isCollapsed('criteria')) {
            <button
              type="button"
              (click)="startEditingCriteria()"
              class="btn-primary flex-shrink-0"
            >
              Edit Criteria
            </button>
          }
        </div>

        @if (!isCollapsed('criteria')) {
          @if (isEditingCriteria) {
            <!-- Edit mode -->
            <div class="space-y-6">
              <div>
                <div class="flex items-center gap-1.5 mb-2">
                  <svg
                    class="w-4 h-4 text-emerald-500 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span class="text-sm font-medium text-emerald-800">Inclusion Criteria</span>
                  <span class="text-[10px] text-emerald-800"
                    >({{ editableInclusionCriteria.length }})</span
                  >
                </div>
                <div class="space-y-2">
                  @for (
                    item of editableInclusionCriteria;
                    track trackByIndex($index);
                    let idx = $index
                  ) {
                    <div class="flex items-start gap-2">
                      <textarea
                        #inclusionInput
                        [(ngModel)]="editableInclusionCriteria[idx]"
                        name="inc-{{ idx }}"
                        class="input-field flex-1 resize-y min-h-[2.5rem]"
                        rows="2"
                        style="field-sizing: content"
                        placeholder="Describe an inclusion criterion…"
                        (keydown)="onInclusionKeydown($event, idx)"
                      ></textarea>
                      <button
                        type="button"
                        (click)="removeInclusionCriterion(idx)"
                        class="btn-small-danger flex-shrink-0"
                        title="Remove"
                      >
                        Remove
                      </button>
                    </div>
                  }
                </div>
                <button
                  type="button"
                  (click)="addInclusionCriterion()"
                  class="mt-2 btn-small-secondary"
                >
                  + Add inclusion criterion
                </button>
              </div>

              <div>
                <div class="flex items-center gap-1.5 mb-2">
                  <svg
                    class="w-4 h-4 text-amber-500 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span class="text-sm font-medium text-amber-800">Exclusion Criteria</span>
                  <span class="text-[10px] text-amber-800"
                    >({{ editableExclusionCriteria.length }})</span
                  >
                </div>
                <div class="space-y-2">
                  @for (
                    item of editableExclusionCriteria;
                    track trackByIndex($index);
                    let idx = $index
                  ) {
                    <div class="flex items-start gap-2">
                      <textarea
                        #exclusionInput
                        [(ngModel)]="editableExclusionCriteria[idx]"
                        name="exc-{{ idx }}"
                        class="input-field flex-1 resize-y min-h-[2.5rem]"
                        rows="2"
                        style="field-sizing: content"
                        placeholder="Describe an exclusion criterion…"
                        (keydown)="onExclusionKeydown($event, idx)"
                      ></textarea>
                      <button
                        type="button"
                        (click)="removeExclusionCriterion(idx)"
                        class="btn-small-danger flex-shrink-0"
                        title="Remove"
                      >
                        Remove
                      </button>
                    </div>
                  }
                </div>
                <button
                  type="button"
                  (click)="addExclusionCriterion()"
                  class="mt-2 btn-small-secondary"
                >
                  + Add exclusion criterion
                </button>
              </div>
            </div>

            @if (criteriaError) {
              <div class="mt-4 text-sm text-red-600">{{ criteriaError }}</div>
            }

            <div class="flex gap-2 mt-0 justify-end">
              <button
                type="button"
                (click)="cancelEditingCriteria()"
                class="btn-small-secondary"
                [disabled]="savingCriteria"
              >
                Cancel
              </button>
              <button
                type="button"
                (click)="saveCriteria()"
                class="btn-small-primary"
                [disabled]="savingCriteria"
              >
                {{ savingCriteria ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          } @else {
            <!-- Read-only view -->
            <div class="space-y-4">
              <!-- Inclusion -->
              <div class="border-l-[3px] border-emerald-400 pl-3">
                <div class="flex items-center gap-1.5 mb-2">
                  <svg
                    class="w-4 h-4 text-emerald-500 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span class="text-xs font-semibold text-emerald-800">Inclusion Criteria</span>
                  <span class="text-[10px] text-emerald-800"
                    >({{ inclusionCriteriaList.length }})</span
                  >
                </div>
                @if (inclusionCriteriaList.length > 0) {
                  <div class="flex flex-wrap gap-1.5">
                    @for (item of inclusionCriteriaList; track trackByIndex($index)) {
                      <span
                        class="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm rounded-full bg-emerald-100 text-slate-900 ring-1 ring-emerald-500/20"
                      >
                        <span
                          class="flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500 text-[9px] font-bold text-white leading-none flex-shrink-0"
                        >
                          {{ $index + 1 }}
                        </span>
                        {{ item }}
                      </span>
                    }
                  </div>
                } @else {
                  <p class="text-xs text-slate-400 italic">No inclusion criteria defined.</p>
                }
              </div>

              <!-- Exclusion -->
              <div class="border-l-[3px] border-amber-400 pl-3">
                <div class="flex items-center gap-1.5 mb-2">
                  <svg
                    class="w-4 h-4 text-amber-500 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span class="text-xs font-semibold text-amber-800">Exclusion Criteria</span>
                  <span class="text-[10px] text-amber-800"
                    >({{ exclusionCriteriaList.length }})</span
                  >
                </div>
                @if (exclusionCriteriaList.length > 0) {
                  <div class="flex flex-wrap gap-1.5">
                    @for (item of exclusionCriteriaList; track trackByIndex($index)) {
                      <span
                        class="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm rounded-full bg-amber-100 text-slate-900 ring-1 ring-amber-500/20"
                      >
                        <span
                          class="flex items-center justify-center w-4 h-4 rounded-full bg-amber-500 text-[9px] font-bold text-white leading-none flex-shrink-0"
                        >
                          {{ $index + 1 }}
                        </span>
                        {{ item }}
                      </span>
                    }
                  </div>
                } @else {
                  <p class="text-xs text-slate-400 italic">No exclusion criteria defined.</p>
                }
              </div>
            </div>
            @if (isProjectPI() && project.state !== 'SETUP' && project.state !== 'INGESTION') {
              <p class="mt-3 text-xs text-amber-600 font-semibold flex items-center gap-1">
                ⚠️ Locked: Screening criteria cannot be modified once the screening phase has
                started.
              </p>
            }
          }
        }
      </section>

      <!-- Exclusion Reasons Section -->
      <section id="exclusion-reasons-section" class="card card-padding">
        @if (warningMessage && !isCollapsed('exclusionReasons')) {
          <app-base-alert
            type="warning"
            title="Exclusion Reasons Notice"
            [message]="warningMessage"
            [closeable]="true"
            (closed)="dismissWarning()"
          ></app-base-alert>
        }

        <div class="flex justify-between items-center mb-4 relative">
          <div class="flex items-start gap-2">
            <button
              type="button"
              (click)="toggleSection('exclusionReasons')"
              class="rounded-lg p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary/40 transition-colors mt-0.5 flex-shrink-0"
              [attr.aria-expanded]="!isCollapsed('exclusionReasons')"
              [attr.aria-label]="
                isCollapsed('exclusionReasons')
                  ? 'Expand Exclusion Reasons'
                  : 'Collapse Exclusion Reasons'
              "
            >
              <svg
                class="w-5 h-5 transition-transform duration-200"
                [class.rotate-180]="!isCollapsed('exclusionReasons')"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <div>
              <div class="flex items-center gap-1.5">
                <h2 class="section-title">Exclusion Reasons</h2>
                <app-info-icon
                  heading="Exclusion Reasons"
                  content="Predefined reasons screeners select when excluding a paper. They ensure traceability for every exclusion and feed into the PRISMA flow diagram."
                  guideAnchor="exclusion-reasons"
                ></app-info-icon>
              </div>
              <p class="text-sm text-gray-600 mt-1 mr-30">
                Define the predefined labels researchers choose from when excluding a paper in
                Full-Text Screening, justifying why it was excluded (e.g. "Wrong population").
                <br />
                Reasons are justifications recorded after exclusion.
              </p>
            </div>
          </div>
          @if (!isEditingReasons && isProjectPI() && !isCollapsed('exclusionReasons')) {
            <button
              type="button"
              (click)="startEditingReasons()"
              class="btn-primary absolute right-0 top-0"
            >
              Edit Reasons
            </button>
          }
        </div>

        @if (!isCollapsed('exclusionReasons')) {
          @if (!isEditingReasons) {
            @if (exclusionReasons.length === 0) {
              <div class="text-center py-6 border border-dashed border-slate-200 rounded-xl">
                <p class="text-gray-600 text-sm">No exclusion reasons defined yet.</p>
              </div>
            } @else {
              <div class="space-y-2">
                @for (reason of exclusionReasons; track reason) {
                  <div
                    class="flex items-center p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-gray-900 font-medium"
                  >
                    <span class="mr-2 text-secondary font-bold">•</span>
                    {{ reason }}
                  </div>
                }
              </div>
            }
          } @else {
            @if (exclusionReasonError) {
              <div
                class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 mb-4"
              >
                {{ exclusionReasonError }}
              </div>
            }

            <form (ngSubmit)="saveExclusionReasons()" class="space-y-4">
              <div class="space-y-3">
                @for (reason of editableExclusionReasons; track $index; let idx = $index) {
                  <div class="flex items-center gap-2">
                    <input
                      #reasonInput
                      type="text"
                      [(ngModel)]="editableExclusionReasons[idx]"
                      name="reason-{{ idx }}"
                      class="input-field"
                      placeholder="e.g. Not matching topic, Incomplete data, etc."
                      required
                      (keydown.enter)="onReasonKeydown($event, idx)"
                    />
                    <button
                      type="button"
                      (click)="removeReasonField(idx)"
                      class="btn-small-danger flex-shrink-0"
                      title="Remove reason field"
                    >
                      Remove
                    </button>
                  </div>
                }
              </div>

              <div class="flex flex-wrap gap-2 pt-2">
                <button type="button" (click)="addReasonField()" class="btn-small-secondary">
                  + Add Reason
                </button>

                <div class="ml-auto flex gap-2">
                  <button
                    type="button"
                    (click)="cancelEditingReasons()"
                    class="btn-small-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" class="btn-small-primary">Save Changes</button>
                </div>
              </div>
            </form>
          }
        }
      </section>

      <!-- Active Screening Locks Section (PI Only) -->
      @if (isProjectPI()) {
        <section class="card card-padding">
          <div class="flex justify-between items-center mb-4 relative">
            <div class="flex items-start gap-2">
              <button
                type="button"
                (click)="toggleSection('locks')"
                class="rounded-lg p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary/40 transition-colors mt-0.5 flex-shrink-0"
                [attr.aria-expanded]="!isCollapsed('locks')"
                [attr.aria-label]="
                  isCollapsed('locks')
                    ? 'Expand Active Screening Locks'
                    : 'Collapse Active Screening Locks'
                "
              >
                <svg
                  class="w-5 h-5 transition-transform duration-200"
                  [class.rotate-180]="!isCollapsed('locks')"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              <div>
                <h2 class="section-title">Active Screening Locks</h2>
                <p class="text-sm text-gray-600 mt-1">
                  View and manage screening locks currently held by researchers. <br />Locks prevent
                  concurrent screening conflicts but can be force-released if a researcher is
                  stalled.
                </p>
              </div>
            </div>
            @if (!isCollapsed('locks')) {
              <button
                type="button"
                (click)="loadProjectLocks()"
                class="btn-secondary absolute right-0 top-0"
              >
                Refresh Locks
              </button>
            }
          </div>

          @if (!isCollapsed('locks')) {
            <div class="mb-3">
              <input
                type="search"
                class="input-field w-full"
                placeholder="Search by paper title, researcher name or email…"
                [value]="lockSearchQuery()"
                (input)="onLockSearchChange($any($event.target).value)"
              />
            </div>

            @if (projectLocks().length === 0) {
              <div class="text-center py-6 border border-dashed border-slate-200 rounded-xl">
                @if (lockSearchQuery().length > 0) {
                  <p class="text-slate-500 text-sm">No locks match your search.</p>
                } @else {
                  <p class="text-slate-500 text-sm">No active screening locks in this project.</p>
                }
              </div>
            } @else {
              <div class="overflow-x-auto">
                <table class="min-w-full table-auto text-sm text-left">
                  <thead>
                    <tr
                      class="border-b border-slate-200 text-slate-500 font-semibold uppercase tracking-wider text-xs"
                    >
                      <th class="pb-3 pr-4">Paper Title</th>
                      <th class="pb-3 px-4">Locked By</th>
                      <th class="pb-3 px-4">Locked Since</th>
                      <th class="pb-3 px-4">Duration</th>
                      <th class="pb-3 px-4 text-center">Stage</th>
                      <th class="pb-3 pl-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100">
                    @for (lock of projectLocks(); track lock.id) {
                      <tr class="hover:bg-slate-50 transition-colors">
                        <td class="py-4 pr-4 align-middle">
                          <div
                            class="font-semibold text-gray-900 line-clamp-2 max-w-md"
                            [title]="lock.paperTitle"
                          >
                            {{ lock.paperTitle }}
                          </div>
                          @if (lock.paperDoi) {
                            <div class="text-xs text-slate-400 mt-0.5">
                              DOI: {{ lock.paperDoi }}
                            </div>
                          }
                        </td>
                        <td class="py-4 px-4 align-middle">
                          <div class="font-medium text-gray-900">
                            {{ lock.userFirstName }} {{ lock.userLastName }}
                          </div>
                          <div class="text-xs text-slate-500">{{ lock.userEmail }}</div>
                        </td>
                        <td class="py-4 px-4 align-middle text-slate-600">
                          {{ lock.lockedAt | date: 'MMM d, yyyy, HH:mm' }}
                        </td>
                        <td class="py-4 px-4 align-middle">
                          <span [class]="getDurationBadgeClass(lock.lockedAt)">
                            {{ getDurationString(lock.lockedAt) }}
                          </span>
                        </td>
                        <td class="py-4 px-4 align-middle text-center">
                          <span
                            class="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-800 border border-blue-200"
                          >
                            {{ formatState(lock.stage) }}
                          </span>
                        </td>
                        <td class="py-4 pl-4 align-middle text-right">
                          <button
                            type="button"
                            (click)="openForceUnlockConfirm(lock)"
                            class="btn-small-danger"
                          >
                            Force Unlock
                          </button>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>

              @if (projectLocksTotalPages() > 1) {
                <div class="flex items-center justify-between px-2 py-3 text-sm text-gray-600">
                  <button
                    type="button"
                    (click)="goToLocksPage(projectLocksCurrentPage() - 1)"
                    [disabled]="projectLocksCurrentPage() === 0"
                    class="btn-small-secondary disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    « Prev
                  </button>
                  <span
                    >Page {{ projectLocksCurrentPage() + 1 }} of
                    {{ projectLocksTotalPages() }}</span
                  >
                  <button
                    type="button"
                    (click)="goToLocksPage(projectLocksCurrentPage() + 1)"
                    [disabled]="projectLocksCurrentPage() + 1 >= projectLocksTotalPages()"
                    class="btn-small-secondary disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Next »
                  </button>
                </div>
              }
            }
          }
        </section>
      }
    }
  </div>
</div>

<!-- Add Researcher Modal -->
<app-add-researcher-modal
  [open]="showAddResearcherModal"
  [projectId]="projectId"
  [availableMembers]="getAvailableMembers()"
  (openChange)="showAddResearcherModal = $event"
  (researcherAdded)="onResearcherAdded($event)"
></app-add-researcher-modal>

<!-- Remove Researcher Confirm Modal -->
<app-confirm-modal
  [open]="showRemoveConfirmModal"
  [title]="'Remove Researcher'"
  [message]="
    'Are you sure you want to remove ' +
    (researcherToRemove?.firstName || '') +
    ' ' +
    (researcherToRemove?.lastName || researcherToRemove?.fullName || '') +
    ' from this project?'
  "
  [confirmText]="'Remove'"
  [cancelText]="'Cancel'"
  [confirmVariant]="'danger'"
  [loading]="removingResearcher"
  (openChange)="showRemoveConfirmModal = $event"
  (confirmed)="confirmRemoveResearcher()"
  (canceled)="cancelRemoveResearcher()"
></app-confirm-modal>

<!-- Confirm Remove Exclusion Reason Modal -->
<app-confirm-modal
  [(open)]="showRemoveReasonModal"
  title="Confirm Removal"
  message="Are you sure you want to remove this exclusion reason from the project?"
  confirmText="Yes, Remove"
  cancelText="Cancel"
  confirmVariant="danger"
  (confirmed)="confirmRemoveReason()"
  (canceled)="cancelRemoveReason()"
></app-confirm-modal>

<!-- Confirm Force Release Lock Modal -->
<app-confirm-modal
  [open]="showForceUnlockConfirmModal"
  [title]="'Force Release Screening Lock'"
  [message]="
    'Are you sure you want to force-release the screening lock held by ' +
    (lockToForceUnlock?.userFirstName || '') +
    ' ' +
    (lockToForceUnlock?.userLastName || '') +
    ' on the paper &quot;' +
    (lockToForceUnlock?.paperTitle || '') +
    '&quot;? This may cause loss of unsaved screening progress for this researcher.'
  "
  [confirmText]="'Force Release'"
  [cancelText]="'Cancel'"
  [confirmVariant]="'danger'"
  [loading]="releasingLock"
  (openChange)="showForceUnlockConfirmModal = $event"
  (confirmed)="confirmForceUnlock()"
  (canceled)="cancelForceUnlock()"
></app-confirm-modal>
